/**
 * Formatting helpers. Control-room displays are read at a glance and under
 * pressure, so durations are short and unpadded ("4m 12s", not "00:04:12") and
 * clock times never carry a date unless the day has changed.
 */

export function duration(seconds: number | null | undefined): string {
  if (seconds == null || Number.isNaN(seconds)) return '—';
  const total = Math.max(0, Math.round(seconds));
  if (total < 60) return `${total}s`;
  const minutes = Math.floor(total / 60);
  const rest = total % 60;
  if (minutes < 60) return rest === 0 ? `${minutes}m` : `${minutes}m ${rest}s`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes === 0 ? `${hours}h` : `${hours}h ${remainingMinutes}m`;
}

/** Elapsed time since an instant, for live "waiting 2m 30s" counters. */
export function since(iso: string | null | undefined, now = Date.now()): number {
  if (!iso) return 0;
  return Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
}

export function elapsed(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return '—';
  return duration(since(iso, now));
}

export function clock(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  const today = new Date();
  const sameDay =
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate();
  return date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: sameDay ? '2-digit' : undefined,
    ...(sameDay ? {} : { day: '2-digit', month: 'short' }),
  });
}

export function dateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString([], {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Groups an E.164 number the way it is read aloud.
 *
 * Short strings are left alone: an emergency number like 15 is what the caller
 * dialled, not a subscriber number, and padding it into a fake international
 * format would be worse than useless on a screen someone reads under pressure.
 */
export function phone(value: string | null | undefined): string {
  if (!value) return '—';
  const trimmed = value.trim();
  if (!trimmed.startsWith('+')) return trimmed;
  const digits = trimmed.slice(1);
  if (digits.length <= 7) return trimmed;

  // +92 300 555 0101 — country code, then operator, then the subscriber block.
  if (digits.startsWith('92') && digits.length === 12) {
    return `+92 ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8)}`;
  }
  return `+${digits.slice(0, digits.length - 7)} ${digits.slice(-7, -4)} ${digits.slice(-4)}`;
}

export function titleCase(value: string | null | undefined): string {
  if (!value) return '—';
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function initials(name: string | null | undefined): string {
  if (!name) return '??';
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

/** Whole number, or one decimal for small values — for report tiles. */
export function stat(value: number | null | undefined, suffix = ''): string {
  if (value == null || Number.isNaN(value)) return '—';
  const rounded = value >= 10 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded}${suffix}`;
}

export function percent(part: number, whole: number): string {
  if (!whole) return '0%';
  return `${Math.round((part / whole) * 100)}%`;
}
